/**
 * Clothes for modular figures: a top and a bottom, each a layer whose pieces hang on the same
 * skeleton as the body — the shirt's body on the torso, its sleeves on the arms, the trousers on
 * the hips and legs — so every garment follows every pose and view. A garment is data (length,
 * sleeve, collar, pattern; length and cut of the legs, guards) plus colours; the drawing is shared.
 *
 * Depth: the shirt's body lies over the trousers' waist, each sleeve just over its own arm (so a
 * far sleeve stays behind the torso), each trouser leg just over its own leg.
 */
import { shade, toHsl, tones } from '../palette';
import {
  type Layer,
  lerp,
  paintCurves,
  type Piece,
  px,
  segment,
  toward,
  torso,
  torsoWidths,
  trunk,
  zOf,
} from './figure';
import { lateral } from './leftArt';
import type { Side, Skeleton } from './skeleton';

type G = CanvasRenderingContext2D;
type Pt = { x: number; y: number };

/**
 * Woven or sewn decoration over a garment's cloth: overlapping armour scales, quilted diamonds,
 * patches, cloud scrolls, a broad hem band, a square badge on the chest, a monk's patchwork, little
 * flowers, pleats or a stripe down the leg, an apron.
 */
export type Pattern =
  | 'scales'
  | 'quilt'
  | 'patch'
  | 'cloud'
  | 'band'
  | 'badge'
  | 'kasaya'
  | 'flowers'
  | 'pleats'
  | 'stripe'
  | 'apron';

export interface TopDesign {
  id: string;
  label: string;
  /** Cut for men, for women, or for anyone (the studio groups them). */
  sex: 'any' | 'male' | 'female';
  /** Where the hem falls: at the waist, over the hips, or down over the thighs. */
  length: 'waist' | 'hips' | 'thigh';
  /** `rolled`: sleeves pushed up to the elbow, forearms bare; `puff`: short, gathered full at the shoulder. */
  sleeve: 'none' | 'short' | 'long' | 'wide' | 'rolled' | 'puff';
  /**
   * `cross`: the wrap collar of hanfu, left panel over right (右衽); `round`: a plain neckband;
   * `stand`: a standing band collar; `open`: the panels laid back in a deep V down to the waist.
   */
  collar: 'cross' | 'round' | 'stand' | 'open';
  /** Leather guards laced over the forearms. */
  bracers?: boolean;
  /** The right sleeve slipped off, the arm and shoulder bare. */
  bareA?: boolean;
  pattern?: Pattern;
}

export interface BottomDesign {
  id: string;
  label: string;
  /** Cut for men, for women, or for anyone (the studio groups them). */
  sex: 'any' | 'male' | 'female';
  length: 'knee' | 'calf' | 'ankle';
  /**
   * `straight` legs, `wide` legs flaring to the ankle, `wrapped`: cloth bands bound round the shins,
   * `lantern`: full legs gathered in at the ankle, `skirt`: one skirt over both legs.
   */
  fit: 'straight' | 'wide' | 'wrapped' | 'lantern' | 'skirt';
  /** A skirt split up the front, the legs showing in a stride. */
  slit?: boolean;
  /** Plates strapped over the shins, or pads on the knees (in the wrap colour). */
  guard?: 'greaves' | 'kneepads';
  pattern?: Pattern;
}

export const TOPS: readonly TopDesign[] = [
  { id: 'cross', label: 'เสื้อป้ายไขว้', sex: 'any', length: 'hips', sleeve: 'long', collar: 'cross' },
  { id: 'tunic', label: 'เสื้อยาวคลุมต้นขา', sex: 'any', length: 'thigh', sleeve: 'long', collar: 'cross' },
  { id: 'wide', label: 'เสื้อแขนกว้าง', sex: 'any', length: 'thigh', sleeve: 'wide', collar: 'cross' },
  { id: 'short', label: 'เสื้อแขนสั้น', sex: 'any', length: 'hips', sleeve: 'short', collar: 'round' },
  { id: 'vest', label: 'เสื้อกั๊กแขนกุด', sex: 'any', length: 'waist', sleeve: 'none', collar: 'cross' },
  { id: 'mandarin', label: 'เสื้อคอตั้ง', sex: 'male', length: 'hips', sleeve: 'long', collar: 'stand' },
  {
    id: 'bracer',
    label: 'เสื้อรัดข้อมือ',
    sex: 'male',
    length: 'hips',
    sleeve: 'long',
    collar: 'cross',
    bracers: true,
  },
  { id: 'rolled', label: 'เสื้อพับแขน', sex: 'male', length: 'hips', sleeve: 'rolled', collar: 'cross' },
  {
    id: 'lamellar',
    label: 'เสื้อเกราะเกล็ด',
    sex: 'male',
    length: 'hips',
    sleeve: 'none',
    collar: 'round',
    pattern: 'scales',
  },
  {
    id: 'quilted',
    label: 'เสื้อนวม',
    sex: 'male',
    length: 'hips',
    sleeve: 'long',
    collar: 'stand',
    pattern: 'quilt',
  },
  {
    id: 'bare',
    label: 'เสื้อเปลือยไหล่',
    sex: 'male',
    length: 'thigh',
    sleeve: 'long',
    collar: 'cross',
    bareA: true,
  },
  { id: 'open', label: 'เสื้อเปิดอก', sex: 'male', length: 'hips', sleeve: 'long', collar: 'open' },
  {
    id: 'patched',
    label: 'เสื้อปะ',
    sex: 'male',
    length: 'hips',
    sleeve: 'short',
    collar: 'round',
    pattern: 'patch',
  },
  { id: 'longvest', label: 'เสื้อกั๊กยาว', sex: 'male', length: 'thigh', sleeve: 'none', collar: 'stand' },
  {
    id: 'armoured',
    label: 'เสื้อเกราะแขนยาว',
    sex: 'male',
    length: 'hips',
    sleeve: 'long',
    collar: 'stand',
    pattern: 'scales',
    bracers: true,
  },
  {
    id: 'ao',
    label: 'เสื้อป้ายสตรีปักดอก',
    sex: 'female',
    length: 'hips',
    sleeve: 'long',
    collar: 'cross',
    pattern: 'flowers',
  },
  {
    id: 'beizi',
    label: 'เสื้อคลุมยาวผ่าหน้า',
    sex: 'female',
    length: 'thigh',
    sleeve: 'wide',
    collar: 'open',
  },
  { id: 'puff', label: 'เสื้อแขนพอง', sex: 'female', length: 'hips', sleeve: 'puff', collar: 'round' },
  {
    id: 'swordmaid',
    label: 'เสื้อจอมยุทธหญิง',
    sex: 'female',
    length: 'hips',
    sleeve: 'long',
    collar: 'stand',
    bracers: true,
  },
  {
    id: 'cloud',
    label: 'เสื้อปักเมฆ',
    sex: 'female',
    length: 'thigh',
    sleeve: 'wide',
    collar: 'cross',
    pattern: 'cloud',
  },
  { id: 'crop', label: 'เสื้อเอวลอย', sex: 'female', length: 'waist', sleeve: 'long', collar: 'cross' },
  {
    id: 'sleevelessf',
    label: 'เสื้อแขนกุดคอตั้ง',
    sex: 'female',
    length: 'waist',
    sleeve: 'none',
    collar: 'stand',
    pattern: 'flowers',
  },
  {
    id: 'longstand',
    label: 'เสื้อคอตั้งตัวยาว',
    sex: 'female',
    length: 'thigh',
    sleeve: 'long',
    collar: 'stand',
    pattern: 'flowers',
  },
  {
    id: 'bandsleeve',
    label: 'เสื้อแขนกว้างขลิบชาย',
    sex: 'female',
    length: 'hips',
    sleeve: 'wide',
    collar: 'cross',
    pattern: 'band',
  },
  {
    id: 'quiltedf',
    label: 'เสื้อนวมสตรี',
    sex: 'female',
    length: 'hips',
    sleeve: 'puff',
    collar: 'stand',
    pattern: 'quilt',
  },
];

export const BOTTOMS: readonly BottomDesign[] = [
  { id: 'trousers', label: 'กางเกงขายาว', sex: 'any', length: 'ankle', fit: 'straight' },
  { id: 'wide', label: 'กางเกงขากว้าง', sex: 'any', length: 'ankle', fit: 'wide' },
  { id: 'wrapped', label: 'กางเกงพันแข้ง', sex: 'any', length: 'ankle', fit: 'wrapped' },
  { id: 'shorts', label: 'กางเกงขาสั้น', sex: 'any', length: 'knee', fit: 'straight' },
  { id: 'lantern', label: 'กางเกงโป่งรัดข้อ', sex: 'male', length: 'ankle', fit: 'lantern' },
  { id: 'calf', label: 'กางเกงสามส่วน', sex: 'male', length: 'calf', fit: 'straight' },
  { id: 'greaves', label: 'กางเกงสนับแข้ง', sex: 'male', length: 'ankle', fit: 'straight', guard: 'greaves' },
  {
    id: 'kneepads',
    label: 'กางเกงสนับเข่า',
    sex: 'male',
    length: 'ankle',
    fit: 'straight',
    guard: 'kneepads',
  },
  { id: 'patched', label: 'กางเกงปะ', sex: 'male', length: 'ankle', fit: 'straight', pattern: 'patch' },
  { id: 'striped', label: 'กางเกงแถบข้าง', sex: 'male', length: 'ankle', fit: 'wide', pattern: 'stripe' },
  {
    id: 'apron',
    label: 'กางเกงผ้ากันเปื้อน',
    sex: 'male',
    length: 'ankle',
    fit: 'wrapped',
    pattern: 'apron',
  },
  { id: 'monk', label: 'กางเกงพระโป่ง', sex: 'male', length: 'ankle', fit: 'lantern', pattern: 'quilt' },
  {
    id: 'wrapguard',
    label: 'กางเกงพันแข้งสนับเข่า',
    sex: 'male',
    length: 'ankle',
    fit: 'wrapped',
    guard: 'kneepads',
  },
  {
    id: 'ridingwide',
    label: 'กางเกงขี่ม้าขากว้าง',
    sex: 'male',
    length: 'ankle',
    fit: 'wide',
    guard: 'kneepads',
  },
  { id: 'skirt', label: 'กระโปรงยาว', sex: 'female', length: 'ankle', fit: 'skirt' },
  { id: 'pleated', label: 'กระโปรงจีบ', sex: 'female', length: 'ankle', fit: 'skirt', pattern: 'pleats' },
  { id: 'shortskirt', label: 'กระโปรงสั้น', sex: 'female', length: 'knee', fit: 'skirt' },
  { id: 'midi', label: 'กระโปรงยาวครึ่งแข้ง', sex: 'female', length: 'calf', fit: 'skirt' },
  { id: 'layered', label: 'กระโปรงสองชั้น', sex: 'female', length: 'ankle', fit: 'skirt', pattern: 'band' },
  {
    id: 'flowerskirt',
    label: 'กระโปรงปักดอก',
    sex: 'female',
    length: 'ankle',
    fit: 'skirt',
    pattern: 'flowers',
  },
  {
    id: 'cloudskirt',
    label: 'กระโปรงลายเมฆ',
    sex: 'female',
    length: 'ankle',
    fit: 'skirt',
    pattern: 'cloud',
  },
  { id: 'splitskirt', label: 'กระโปรงผ่าหน้า', sex: 'female', length: 'ankle', fit: 'skirt', slit: true },
  { id: 'culottes', label: 'กางเกงกระโปรง', sex: 'female', length: 'ankle', fit: 'wide', pattern: 'pleats' },
  { id: 'slim', label: 'กางเกงรัดข้อสตรี', sex: 'female', length: 'ankle', fit: 'lantern' },
];

/** A worn top: its design, cloth colour, trim (collar, cuffs, hem) and the inner collar's colour. */
export interface TopLook {
  id: string;
  colour: string;
  trim: string;
  inner: string;
  /** A second piping just inside the trim, at the collar and cuffs. */
  accent?: string;
  brocade?: Brocade;
}

/** Worn trousers: design, cloth colour and the colour of shin wraps (and guards, stripes, aprons). */
export interface BottomLook {
  id: string;
  colour: string;
  wrap: string;
}

export const DEFAULT_TOP: TopLook = { id: 'cross', colour: '#3b55a3', trim: '#e8c27e', inner: '#f4ecd6' };
export const DEFAULT_BOTTOM: BottomLook = { id: 'wrapped', colour: '#2a2438', wrap: '#cfc4ae' };

/** Cloth and its shade, the shade derived the way every other ramp is (see palette.ts). */
function cloth(colour: string): { c: string; s: string } {
  const t = tones({ outer: colour });
  return { c: colour, s: t.outerS! };
}

const LEATHER = { c: '#5a3e2a', s: '#3e2a1c' };

/** Which way the right side (A) lies across the screen: +1 right, -1 left, 0 when stacked. */
const sideOf = (sk: Skeleton, s: Side) => Math.sign(sk.joints[`shoulder${s}`].x - sk.joints.chest.x);

/** Each row of the torso as drawn by `torso()` with the same `grow`, as left and right columns. */
function torsoRows(
  sk: Skeleton,
  grow: number,
  to: 'waist' | 'pelvis',
): { y: number; L: number; R: number }[] {
  const j = sk.joints;
  const [shw, whw, hhw] = torsoWidths(sk);
  const rows = Math.max(1, j.waist.y - j.chest.y);
  const out: { y: number; L: number; R: number }[] = [];
  const span = (top: Pt, bottom: Pt, hw: (t: number) => number) => {
    const y0 = Math.round(top.y);
    const y1 = Math.round(bottom.y);
    for (let y = y0; y <= y1; y++) {
      const t = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
      const x = top.x + (bottom.x - top.x) * t;
      const w = hw(t);
      if (!out.some((r) => r.y === y)) out.push({ y, L: Math.round(x - w), R: Math.round(x + w) });
    }
  };
  span(j.chest, j.waist, (t) => Math.min(shw + (whw - shw) * t, shw - 1 + t * rows) + grow);
  if (to === 'pelvis') span(j.waist, j.pelvis, (t) => whw + (hhw - whw) * t + grow);
  return out;
}

/** A small cloud scroll, three by two, in `c`. */
function cloudAt(g: G, x: number, y: number, c: string): void {
  px(g, x, y, 2, 1, c);
  px(g, x + 1, y + 1, 2, 1, c);
  px(g, x - 1, y + 1, 1, 1, c);
}

/**
 * Lays `pattern` over cloth already drawn in `rows` (left and right column per row): decoration
 * stays a pixel inside the edges so the garment's outline is untouched.
 */
function decorate(
  g: G,
  sk: Skeleton,
  rows: readonly { y: number; L: number; R: number }[],
  pattern: Pattern | undefined,
  c: string,
  s: string,
  trim: string,
  inner: string,
): void {
  if (!pattern || !rows.length) return;
  const top = rows[0]!.y;
  const n = rows.length;
  rows.forEach(({ y, L, R }, i) => {
    const r = y - top;
    for (let x = L + 1; x < R; x++) {
      let col: string | undefined;
      switch (pattern) {
        case 'scales':
          // Overlapping plates: a shaded lower edge every other row, staggered row to row.
          if (r % 2 === 1 && (x + (r % 4 === 1 ? 0 : 2)) % 4 !== 0) col = s;
          else if (r % 2 === 0 && (x + (r % 4 === 0 ? 0 : 2)) % 4 === 0) col = trim;
          break;
        case 'quilt':
          if ((x + y) % 4 === 0 || (x - y) % 4 === 0) col = s;
          break;
        case 'kasaya':
          // A monk's patchwork: panels sewn in a grid, a diagonal band from the left shoulder.
          if (r % 6 === 5 || (x - L) % 6 === 5) col = s;
          break;
        case 'pleats':
          if ((x - L) % 3 === 1 && r > 1) col = s;
          break;
        case 'band':
          if (i >= n - 3 && i < n - 1) col = trim;
          else if (i === n - 4) col = inner;
          break;
        default:
          break;
      }
      if (col) px(g, x, y, 1, 1, col);
    }
  });
  const mid = rows[Math.floor(n / 2)]!;
  const cx = Math.round((mid.L + mid.R) / 2);
  switch (pattern) {
    case 'patch': {
      // Two patches of a lighter and a darker cloth, stitched at the corners.
      const a = rows[Math.min(n - 1, 2)]!;
      px(g, a.L + 2, a.y, 3, 3, shade(c, 0.1));
      px(g, a.L + 2, a.y, 1, 1, s);
      px(g, a.L + 4, a.y + 2, 1, 1, s);
      const b = rows[Math.max(0, n - 5)]!;
      px(g, b.R - 4, b.y, 3, 2, shade(c, -0.08));
      px(g, b.R - 2, b.y, 1, 1, trim);
      break;
    }
    case 'cloud':
      for (const [k, dx] of [
        [0.25, -3],
        [0.55, 2],
        [0.8, -2],
      ] as const) {
        const row = rows[Math.min(n - 1, Math.floor(n * k))]!;
        const x = Math.round((row.L + row.R) / 2) + dx;
        if (x - 1 > row.L && x + 2 < row.R) cloudAt(g, x, row.y, trim);
      }
      break;
    case 'flowers':
      for (let i = 2; i < n - 1; i += 4) {
        const row = rows[i]!;
        const w = row.R - row.L - 3;
        if (w < 2) continue;
        const x = row.L + 2 + ((i * 5) % w);
        px(g, x, row.y, 1, 1, trim);
        px(g, x - 1, row.y + 1, 3, 1, trim);
        px(g, x, row.y + 1, 1, 1, inner);
        px(g, x, row.y + 2, 1, 1, trim);
      }
      break;
    case 'badge':
      // A square of embroidery on the chest (a rank badge): trimmed, with a bird in the middle.
      if (!sk.back) {
        const row = rows[Math.min(n - 1, 3)]!;
        const bx = Math.round((row.L + row.R) / 2) + (sk.square ? 0 : 1) - 3;
        px(g, bx, row.y, 7, 6, trim);
        px(g, bx + 1, row.y + 1, 5, 4, inner);
        px(g, bx + 2, row.y + 2, 3, 1, s);
        px(g, bx + 3, row.y + 3, 2, 1, s);
      }
      break;
    case 'kasaya': {
      // The sash of the kasaya over the left shoulder (on a left-facing view three-quarter on, the mirror
      // image's: from the other shoulder, rig/leftArt.ts).
      const first = rows[0]!;
      const last = rows[n - 1]!;
      const fromL = sk.back === (lateral(sk) > 0);
      const from = { x: fromL ? first.R - 2 : first.L + 2, y: first.y };
      const to = { x: fromL ? last.L + 2 : last.R - 2, y: last.y };
      for (let k = 0; k <= 1; k += 1 / 20) {
        const p = lerp(from, to, k);
        px(g, p.x - 1, p.y, 3, 1, trim);
      }
      break;
    }
    case 'apron':
      if (!sk.back) {
        const row = rows[0]!;
        const w = Math.max(4, Math.round((row.R - row.L) * 0.6));
        const x = cx - Math.floor(w / 2) + (sk.square ? 0 : 1);
        for (const rr of rows) px(g, x, rr.y, w, 1, inner);
        px(g, x, row.y, w, 1, trim);
      }
      break;
    default:
      break;
  }
}

// ── tops ──

export function topLayer(look: TopLook): Layer {
  const design = TOPS.find((t) => t.id === look.id);
  const { c, s } = cloth(look.colour);
  return {
    id: 'top',
    label: 'เสื้อ',
    pieces(sk) {
      if (!design) return [];
      const out: Piece[] = [{ z: zOf(sk, 'chest', 0.03), draw: (g) => shirtBody(g, sk, design, look, c, s) }];
      if (design.sleeve !== 'none')
        for (const side of ['B', 'A'] as Side[]) {
          if (side === 'A' && design.bareA) continue;
          out.push({
            z: zOf(sk, `shoulder${side}`, 0.03),
            draw: (g) => sleeve(g, sk, side, design, look, c, s),
          });
        }
      return out;
    },
  };
}

function shirtBody(g: G, sk: Skeleton, d: TopDesign, look: TopLook, c: string, s: string): void {
  const j = sk.joints;
  const [, , hhw] = torsoWidths(sk);
  const to = d.length === 'waist' ? 'waist' : 'pelvis';
  torso(g, sk, c, s, 1, to);
  const rows = torsoRows(sk, 1, to);
  let hemY = Math.round(d.length === 'waist' ? j.waist.y : j.pelvis.y);
  let hemHw = hhw + 1;
  if (d.length === 'thigh') {
    // The skirt of a long top, flaring a little as it falls over the thighs.
    const end = { x: j.pelvis.x, y: j.pelvis.y + 8 };
    trunk(g, j.pelvis, end, (t) => hhw + 1 + t * 1.5, c, { c: s, left: 2 });
    paintCurves(g, sk, 1.5, 'seat', c, s, Math.round(end.y));
    for (let y = Math.round(j.pelvis.y) + 1; y <= Math.round(end.y); y++) {
      const t = (y - j.pelvis.y) / 8;
      const hw = hhw + 1 + t * 1.5;
      rows.push({ y, L: Math.round(j.pelvis.x - hw), R: Math.round(j.pelvis.x + hw) });
    }
    hemY = Math.round(end.y);
    hemHw = hhw + 2.5;
  }
  decorate(g, sk, rows, d.pattern, c, s, look.trim, look.inner);
  if (look.brocade) weave(g, rows, look.brocade, c);
  px(g, Math.round(j.pelvis.x - hemHw), hemY, Math.round(hemHw * 2) + 1, 1, look.trim);
  collar(g, sk, d, look);
}

/** The collar: a wrap collar's diagonal edge with the inner robe showing in the V, or a neckband. */
function collar(g: G, sk: Skeleton, d: Pick<TopDesign, 'collar' | 'bareA'>, look: TopLook): void {
  const j = sk.joints;
  const top = Math.round(j.chest.y) - 1;
  if (d.collar === 'stand') {
    // A band standing up round the neck, seen all the way round.
    px(g, Math.round(j.neck.x) - 2, top - 1, 5, 2, look.trim);
    if (look.accent) px(g, Math.round(j.neck.x) - 2, top, 5, 1, look.accent);
    if (!sk.back) px(g, Math.round(j.neck.x) + (sk.square ? 0 : 1), top + 1, 1, 3, look.trim);
    if (look.accent && !sk.back)
      px(g, Math.round(j.neck.x) + (sk.square ? 0 : 1) + 1, top + 1, 1, 3, look.accent);
    return;
  }
  if (sk.back) {
    px(g, Math.round(j.neck.x) - 2, top, 5, 1, look.trim);
    return;
  }
  if (d.collar === 'round') {
    px(g, Math.round(j.neck.x) - 2, top, 5, 1, look.trim);
    px(g, Math.round(j.neck.x) - 1, top + 1, 3, 1, look.trim);
    return;
  }
  const a = sideOf(sk, 'A') || 1;
  if (d.collar === 'open') {
    // Both panels laid back from the neck to the sash, the inner layer (or skin) bare between.
    const bottom = Math.round(j.waist.y) - 1;
    const mid = j.chest.x + (sk.square ? 0 : sk.view === 'side' ? 3 : 1);
    for (let y = top; y <= bottom; y++) {
      const t = (y - top) / Math.max(1, bottom - top);
      const hw = Math.round(2.5 * (1 - t) + 0.5);
      if (sk.view === 'side') {
        px(g, mid - hw, y, hw + 1, 1, look.inner);
        px(g, mid - hw - 1, y, 1, 1, look.trim);
      } else {
        px(g, mid - hw, y, hw * 2 + 1, 1, look.inner);
        px(g, mid - hw - 1, y, 1, 1, look.trim);
        px(g, mid + hw + 1, y, 1, 1, look.trim);
      }
    }
    return;
  }
  if (sk.view === 'side') {
    // In profile the wrap's edge runs from the front of the neck down to the chest.
    line(g, { x: j.neck.x + 1, y: top }, { x: j.chest.x + 3, y: top + 5 }, look.trim);
    if (look.accent) line(g, { x: j.neck.x, y: top + 1 }, { x: j.chest.x + 2, y: top + 6 }, look.accent);
    return;
  }
  // The left panel (B) crosses over to the right (A): its edge runs from the neck on the B side
  // down to under the right arm, the inner robe showing above it.
  const from = { x: j.neck.x - a, y: top };
  const to = { x: j.chest.x + a * 4, y: top + 7 };
  line(g, { x: from.x + a, y: from.y }, { x: to.x + a, y: to.y - 1 }, look.inner);
  if (look.accent) line(g, { x: from.x - a, y: from.y + 1 }, { x: to.x - a, y: to.y + 1 }, look.accent);
  line(g, from, to, look.trim);
  if (d.bareA) {
    // The slipped sleeve gathered round the waist on the bare side.
    const sh = j.shoulderA;
    px(g, Math.round(sh.x) - 2, Math.round(sh.y) - 1, 4, 1, look.trim);
  }
}

/**
 * A band one pixel deep across a limb at `at`, `w` wide, square to the limb running from `from`
 * (cuffs, hems, the edge of a sleeve's lining).
 */
function band(g: G, from: Pt, at: Pt, w: number, colour: string): void {
  const dx = at.x - from.x;
  const dy = at.y - from.y;
  const l = Math.max(0.001, Math.hypot(dx, dy));
  const nx = -dy / l;
  const ny = dx / l;
  const n = Math.max(1, Math.ceil(w));
  for (let i = 0; i <= n; i++) {
    const k = i / n - 0.5;
    px(g, at.x + nx * k * w - 0.5, at.y + ny * k * w - 0.5, 1, 1, colour);
  }
}

function line(g: G, a: Pt, b: Pt, colour: string): void {
  const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    px(g, a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, 1, 1, colour);
  }
}

function sleeve(g: G, sk: Skeleton, side: Side, d: TopDesign, look: TopLook, c: string, s: string): void {
  const j = sk.joints;
  const L = sk.shape.limb;
  const sh = j[`shoulder${side}`];
  const el = j[`elbow${side}`];
  const wr = j[`wrist${side}`];
  if (d.sleeve === 'short') {
    const end = lerp(sh, el, 0.7);
    segment(g, sh, end, 5 + L, 5 + L, c, s);
    band(g, sh, toward(end, sh, -1.5), 5 + L, look.trim);
    return;
  }
  if (d.sleeve === 'puff') {
    // Gathered full at the shoulder, caught in a band above the elbow.
    const end = lerp(sh, el, 0.65);
    segment(g, sh, lerp(sh, end, 0.5), 6 + L, 7 + L, c, s);
    segment(g, lerp(sh, end, 0.5), end, 7 + L, 5 + L, c, s);
    band(g, sh, toward(end, sh, -1), 5 + L, look.trim);
    return;
  }
  if (d.sleeve === 'rolled') {
    // Pushed up past the elbow, the turned-back cuff a thick roll.
    const end = lerp(el, wr, 0.1);
    segment(g, sh, end, 5 + L, 5 + L, c, s);
    band(g, sh, toward(end, sh, -1), 6 + L, look.inner);
    band(g, sh, toward(end, sh, -2), 6 + L, s);
    return;
  }
  segment(g, sh, el, 5 + L, (d.sleeve === 'wide' ? 6 : 5) + L, c, s);
  // Fine cloth: wrinkles bunched at the crook of the elbow, a lit fold above them.
  const fine = !!look.brocade;
  if (fine) {
    band(g, sh, lerp(sh, el, 0.82), 3, s);
    band(g, sh, lerp(sh, el, 0.7), 3, shade(c, 0.06));
  }
  if (d.sleeve === 'long') {
    const end = toward(wr, el, 1);
    segment(g, el, end, 5 + L, 4 + L, c, s);
    if (fine) band(g, el, lerp(el, end, 0.3), 3, s);
    if (d.bracers) {
      // Leather guards from mid-forearm to the wrist, laced, a trimmed edge at each end.
      const from = lerp(el, end, 0.35);
      segment(g, from, end, 5 + L, 5 + L, LEATHER.c, LEATHER.s);
      band(g, el, from, 5 + L, look.trim);
      band(g, el, lerp(from, end, 0.5), 5 + L, LEATHER.s);
      band(g, el, toward(end, el, -1), 5 + L, look.trim);
      return;
    }
    band(g, el, toward(end, el, -1.5), 4 + L, look.trim);
    if (look.accent) band(g, el, toward(end, el, -3), 4 + L, look.accent);
    return;
  }
  // A wide sleeve: it widens down the forearm and hangs open in a bag below it, its lining at the cuff.
  const mid = lerp(el, wr, 0.4);
  const cuff = toward(wr, el, 1);
  segment(g, { x: mid.x, y: mid.y + 2 }, { x: cuff.x, y: cuff.y + 2 }, 5 + L, 6 + L, s, s);
  segment(g, el, cuff, 6 + L, 7 + L, c, s);
  // The open cuff: a trimmed edge with the lining just inside it.
  band(g, el, toward(cuff, el, 1), 7, look.inner);
  band(g, el, toward(cuff, el, -2.5), 7, look.trim);
  if (look.accent) {
    band(g, el, toward(cuff, el, -4), 7, look.accent);
    band(g, el, toward(cuff, el, -5), 7, look.trim);
    if (fine) {
      // An embroidered band above the cuff: a row of stitches between two lines of the trim.
      const at = toward(cuff, el, -6);
      for (let k = -3; k <= 3; k += 2) {
        const dx = at.x - el.x;
        const dy = at.y - el.y;
        const l = Math.max(0.001, Math.hypot(dx, dy));
        px(g, at.x + (-dy / l) * k - 0.5, at.y + (dx / l) * k - 0.5, 1, 1, look.accent);
      }
      band(g, el, toward(cuff, el, -7), 7, look.trim);
    }
  }
  if (fine) band(g, el, lerp(el, cuff, 0.25), 4, s);
}

// ── bottoms ──

export function bottomLayer(look: BottomLook): Layer {
  const design = BOTTOMS.find((b) => b.id === look.id);
  const { c, s } = cloth(look.colour);
  const wrap = cloth(look.wrap);
  return {
    id: 'bottom',
    label: 'กางเกง',
    pieces(sk) {
      if (!design) return [];
      const j = sk.joints;
      const [, whw, hhw] = torsoWidths(sk);
      if (design.fit === 'skirt')
        return [{ z: zOf(sk, 'chest', 0.02), draw: (g) => skirt(g, sk, design, look, c, s) }];
      const out: Piece[] = [
        {
          // The waist and seat, under the shirt's hem.
          z: zOf(sk, 'chest', 0.015),
          draw: (g) => {
            const seat = { x: j.pelvis.x, y: j.pelvis.y + 3 };
            trunk(g, j.waist, seat, (t) => whw + 0.5 + (hhw + 1 - whw - 0.5) * t, c, { c: s, left: 2 });
            paintCurves(g, sk, 1, 'seat', c, s, Math.round(seat.y));
          },
        },
      ];
      for (const side of ['B', 'A'] as Side[])
        out.push({
          z: zOf(sk, `hip${side}`, 0.02),
          draw: (g) => trouserLeg(g, sk, side, design, c, s, wrap),
        });
      if (design.pattern === 'apron')
        out.push({
          z: zOf(sk, 'chest', 0.04),
          draw: (g) => {
            if (sk.back) return;
            // A cloth apron tied at the waist, hanging to the knee in front.
            const knee = (j.kneeA.y + j.kneeB.y) / 2;
            const rows: { y: number; L: number; R: number }[] = [];
            const cx = j.waist.x + (sk.square ? 0 : sk.view === 'side' ? whw - 1 : 1);
            const hw = sk.view === 'side' ? 1.5 : whw;
            for (let y = Math.round(j.waist.y); y <= Math.round(knee); y++)
              rows.push({ y, L: Math.round(cx - hw), R: Math.round(cx + hw) });
            for (const r of rows) px(g, r.L, r.y, r.R - r.L + 1, 1, wrap.c);
            for (const r of rows) px(g, r.L, r.y, 1, 1, wrap.s);
            px(g, rows[0]!.L, rows[0]!.y, rows[0]!.R - rows[0]!.L + 1, 1, wrap.s);
          },
        });
      return out;
    },
  };
}

/**
 * How far the legs reach sideways at row `y` (both legs, thigh to ankle, at the new body's widths: newBody.ts
 * `legParts`), or null above the hips: a skirt is widened to cover them, so in a stride the forward thigh and knee
 * stay under the cloth.
 */
function legReach(sk: Skeleton, y: number): { L: number; R: number } | null {
  const j = sk.joints;
  const lw = sk.shape.limb / 2;
  let L = Infinity;
  let R = -Infinity;
  for (const side of ['A', 'B'] as Side[]) {
    const hip = j[`hip${side}`];
    const knee = j[`knee${side}`];
    const ankle = j[`ankle${side}`];
    const seg = (a: { x: number; y: number }, b: { x: number; y: number }, hw: number) => {
      if (y < Math.min(a.y, b.y) || y > Math.max(a.y, b.y) || a.y === b.y) return;
      const x = a.x + ((b.x - a.x) * (y - a.y)) / (b.y - a.y);
      L = Math.min(L, x - hw);
      R = Math.max(R, x + hw);
    };
    seg(hip, knee, 3.2 + lw);
    seg(knee, ankle, 2.6 + lw);
  }
  return Number.isFinite(L) ? { L, R } : null;
}

/** A skirt from the waist over both legs, wide enough to clear a stride, its hem in shade. */
function skirt(g: G, sk: Skeleton, d: BottomDesign, look: BottomLook, c: string, s: string): void {
  const j = sk.joints;
  const [, whw, hhw] = torsoWidths(sk);
  const feet = { x: (j.ankleA.x + j.ankleB.x) / 2, y: Math.max(j.ankleA.y, j.ankleB.y) };
  const knees = { x: (j.kneeA.x + j.kneeB.x) / 2, y: (j.kneeA.y + j.kneeB.y) / 2 + 1 };
  // Where the hem falls, and how much of the stride it has to clear there.
  const reach = d.length === 'ankle' ? 1 : d.length === 'calf' ? 0.55 : 0;
  const hem = lerp(knees, feet, reach);
  const stride = Math.abs(j.ankleA.x - j.ankleB.x) * (0.6 + 0.4 * reach);
  const hemHw = Math.max(hhw + 2.5 + 1.5 * reach, stride / 2 + 3);
  const y0 = Math.round(j.waist.y);
  const y1 = Math.round(hem.y);
  const facing = sk.square ? 0 : 1;
  const rows: { y: number; L: number; R: number }[] = [];
  for (let y = y0; y <= y1; y++) {
    const t = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
    const cx = j.waist.x + (hem.x - j.waist.x) * t;
    const hw = whw + 0.5 + (hemHw - whw - 0.5) * Math.pow(t, 0.8);
    // Never narrower than the legs under it: a stride's forward thigh and knee stay covered.
    const legs = legReach(sk, y);
    const L = Math.min(Math.round(cx - hw), legs ? Math.floor(legs.L) : Infinity);
    const R = Math.max(Math.round(cx + hw), legs ? Math.ceil(legs.R) : -Infinity);
    // A split skirt opens up the front from the knee down, the legs showing through.
    const gap = d.slit && !sk.back && t > 0.4 ? Math.round((t - 0.4) * (sk.view === 'side' ? 2 : 4)) : -1;
    const mid = Math.round(cx + facing * (sk.view === 'side' ? hw - 2 : 2));
    for (let x = L; x <= R; x++) {
      if (Math.abs(x - mid) <= gap) continue;
      const col = gap >= 0 && Math.abs(x - mid) === gap + 1 ? look.wrap : y === y1 || x < L + 2 ? s : c;
      px(g, x, y, 1, 1, col);
    }
    rows.push({ y, L, R });
  }
  // A full seat fills out the back of the skirt.
  paintCurves(g, sk, 2, 'seat', c, s);
  decorate(g, sk, rows, d.pattern, c, s, look.wrap, look.wrap);
  // A waistband.
  px(g, rows[0]!.L, y0, rows[0]!.R - rows[0]!.L + 1, 1, s);
}

function trouserLeg(
  g: G,
  sk: Skeleton,
  side: Side,
  d: BottomDesign,
  c: string,
  s: string,
  wrap: { c: string; s: string },
): void {
  const j = sk.joints;
  const L = sk.shape.limb;
  const hip = j[`hip${side}`];
  const knee = j[`knee${side}`];
  const ankle = j[`ankle${side}`];
  if (d.length === 'knee') {
    const end = lerp(hip, knee, 0.9);
    segment(g, hip, end, 7 + L, 6 + L, c, s);
    band(g, hip, toward(end, hip, -2.5), 6 + L, s);
    return;
  }
  segment(g, hip, knee, 7 + L, 6 + L, c, s);
  if (d.pattern === 'patch' && side === 'A') {
    const p = lerp(hip, knee, 0.5);
    px(g, p.x - 1, p.y - 1, 3, 3, shade(c, 0.1));
    px(g, p.x - 1, p.y - 1, 1, 1, s);
  }
  if (d.pattern === 'stripe') {
    // A stripe of the wrap colour down the outside of each leg.
    const out = (sideOf(sk, side) || 1) * (sk.view === 'side' ? 0 : 1);
    line(g, { x: hip.x + out * 2.5, y: hip.y }, { x: ankle.x + out * 3.5, y: ankle.y }, wrap.c);
  }
  if (d.length === 'calf') {
    const end = lerp(knee, ankle, 0.55);
    segment(g, knee, end, 6 + L, 6 + L, c, s);
    band(g, knee, toward(end, knee, -1.5), 6 + L, s);
    return;
  }
  if (d.fit === 'wide') {
    // Wide legs flare to the ankle and cover the top of the foot.
    segment(g, knee, { x: ankle.x, y: ankle.y + 1 }, 6 + L, 8 + L, c, s);
    if (d.pattern === 'pleats')
      for (const k of [-2, 1])
        line(g, { x: hip.x + k, y: hip.y + 2 }, { x: ankle.x + k * 1.5, y: ankle.y }, s);
    if (d.pattern === 'stripe') {
      const out = (sideOf(sk, side) || 1) * (sk.view === 'side' ? 0 : 1);
      line(g, { x: knee.x + out * 3, y: knee.y }, { x: ankle.x + out * 3.5, y: ankle.y }, wrap.c);
    }
    return;
  }
  if (d.fit === 'lantern') {
    // Full through the shin, gathered into a band just above the ankle.
    const end = toward(ankle, knee, 1.5);
    segment(g, knee, lerp(knee, end, 0.6), 7 + L, 8 + L, c, s);
    segment(g, lerp(knee, end, 0.6), end, 8 + L, 6 + L, c, s);
    band(g, knee, end, 5 + L, wrap.c);
    band(g, knee, toward(end, knee, -1), 5 + L, wrap.s);
    return;
  }
  const end = toward(ankle, knee, 0.5);
  segment(g, knee, end, 6 + L, 5 + L, c, s);
  if (d.fit === 'wrapped') {
    // Shin wraps bound from below the knee to the ankle, the turns of the cloth showing as bands.
    const from = lerp(knee, ankle, 0.3);
    segment(g, from, end, 5 + L, 5 + L, wrap.c, wrap.s);
    for (const t of [0.45, 0.65, 0.85]) {
      const p = lerp(knee, ankle, t);
      px(g, p.x - 2, p.y, 4, 1, wrap.s);
    }
    return;
  }
  band(g, knee, toward(end, knee, -2), 5 + L, s);
  if (d.guard === 'greaves') {
    // Plates strapped over the shin, a ridge down the middle and a rim at the top.
    const from = lerp(knee, ankle, 0.15);
    const to = lerp(knee, ankle, 0.85);
    segment(g, from, to, 6 + L, 5 + L, wrap.c, wrap.s);
    band(g, knee, from, 6 + L, wrap.s);
    line(g, lerp(from, to, 0.15), to, shade(wrap.c, 0.12));
  } else if (d.guard === 'kneepads') {
    px(g, knee.x - 2, knee.y - 1, 4, 3, wrap.c);
    px(g, knee.x - 2, knee.y + 1, 4, 1, wrap.s);
  }
}

// ── robes: the outermost layer, over shirt and trousers ──

export interface RobeDesign {
  id: string;
  label: string;
  /** Cut for men, for women, or for anyone (the studio groups them). */
  sex: 'any' | 'male' | 'female';
  length: 'knee' | 'ankle';
  sleeve: 'none' | 'long' | 'wide';
  /**
   * `open`: the front parts below the sash, showing what is worn underneath; `slit`: closed, but
   * split up the front to mid-thigh for riding and fighting.
   */
  front: 'closed' | 'open' | 'slit';
  collar?: 'cross' | 'stand' | 'fur';
  pattern?: Pattern;
  /** The sash tied high under the chest, the skirt falling from there (a woman's ruqun). */
  highWaist?: boolean;
  /** The hem trails on the ground behind. */
  train?: boolean;
}

export const ROBES: readonly RobeDesign[] = [
  { id: 'robe', label: 'ชุดคลุมยาว', sex: 'any', length: 'ankle', sleeve: 'long', front: 'closed' },
  { id: 'hanfu', label: 'ฮั่นฝู', sex: 'any', length: 'ankle', sleeve: 'wide', front: 'open' },
  { id: 'coat', label: 'เสื้อคลุมยาวแขนกุด', sex: 'any', length: 'knee', sleeve: 'none', front: 'open' },
  {
    id: 'daopao',
    label: 'ชุดนักพรต',
    sex: 'male',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    pattern: 'band',
  },
  {
    id: 'kasaya',
    label: 'จีวรพาดบ่า',
    sex: 'male',
    length: 'ankle',
    sleeve: 'long',
    front: 'closed',
    pattern: 'kasaya',
  },
  {
    id: 'feiyu',
    label: 'ชุดปลาบิน',
    sex: 'male',
    length: 'ankle',
    sleeve: 'long',
    front: 'slit',
    collar: 'stand',
    pattern: 'cloud',
  },
  {
    id: 'battle',
    label: 'ชุดศึกผ่าหน้า',
    sex: 'male',
    length: 'knee',
    sleeve: 'long',
    front: 'slit',
    pattern: 'scales',
  },
  {
    id: 'scholar',
    label: 'ชุดบัณฑิต',
    sex: 'male',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    collar: 'stand',
    pattern: 'band',
  },
  {
    id: 'furcloak',
    label: 'เสื้อคลุมคอขน',
    sex: 'male',
    length: 'knee',
    sleeve: 'long',
    front: 'open',
    collar: 'fur',
  },
  {
    id: 'court',
    label: 'ชุดขุนนาง',
    sex: 'male',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    collar: 'stand',
    pattern: 'badge',
  },
  { id: 'travel', label: 'ชุดเดินทาง', sex: 'male', length: 'knee', sleeve: 'long', front: 'slit' },
  {
    id: 'armour',
    label: 'ชุดเกราะยาว',
    sex: 'male',
    length: 'ankle',
    sleeve: 'long',
    front: 'slit',
    collar: 'stand',
    pattern: 'scales',
  },
  {
    id: 'hermit',
    label: 'ชุดฤๅษีปะ',
    sex: 'male',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    pattern: 'patch',
  },
  {
    id: 'ruqun',
    label: 'ชุดสตรีเอวสูง',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    highWaist: true,
    pattern: 'flowers',
  },
  {
    id: 'trailing',
    label: 'ชุดหางยาว',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'open',
    train: true,
    pattern: 'flowers',
  },
  {
    id: 'palace',
    label: 'ชุดนางใน',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    highWaist: true,
    pattern: 'cloud',
  },
  {
    id: 'swordlady',
    label: 'ชุดจอมยุทธหญิง',
    sex: 'female',
    length: 'ankle',
    sleeve: 'long',
    front: 'slit',
    collar: 'stand',
  },
  {
    id: 'fairy',
    label: 'ชุดเซียน',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'open',
    train: true,
    pattern: 'band',
  },
  {
    id: 'dancer',
    label: 'ชุดนางรำ',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'slit',
    highWaist: true,
    pattern: 'flowers',
  },
  {
    id: 'priestess',
    label: 'ชุดนักพรตหญิง',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    collar: 'stand',
    pattern: 'band',
  },
  {
    id: 'furcape',
    label: 'เสื้อคลุมขนสตรี',
    sex: 'female',
    length: 'ankle',
    sleeve: 'long',
    front: 'open',
    collar: 'fur',
  },
  {
    id: 'longopen',
    label: 'ชุดคลุมเปิดยาว',
    sex: 'female',
    length: 'ankle',
    sleeve: 'long',
    front: 'open',
    pattern: 'flowers',
  },
  {
    id: 'snow',
    label: 'ชุดขาวหิมะ',
    sex: 'female',
    length: 'ankle',
    sleeve: 'wide',
    front: 'closed',
    highWaist: true,
    pattern: 'band',
  },
];

/**
 * Embroidery round a robe's hem: sea waves breaking on rocks (the court robes' 海水江崖), cloud
 * scrolls, flames, a row of lotus, bamboo or peonies.
 */
export type HemMotif = 'waves' | 'clouds' | 'flames' | 'lotus' | 'bamboo' | 'peony';

/**
 * Figured silk: a small motif woven into the cloth itself, a shade lighter and darker than the
 * ground (a lattice of diamonds, cloud scrolls, fish scales, scattered blossoms, a key fret).
 */
export type Brocade = 'lattice' | 'clouds' | 'scales' | 'blossoms' | 'fret';

/** A worn robe: design, cloth, trim, the lining seen in its cuffs and opening, and the sash. */
export interface RobeLook {
  id: string;
  colour: string;
  trim: string;
  inner: string;
  sash: string;
  /** A second colour of thread: piping inside the trim, and the hem embroidery's second colour. */
  accent?: string;
  hem?: HemMotif;
  /** Figured silk over the whole robe; with it (or a hem) the robe is drawn in its fine detail. */
  brocade?: Brocade;
  /**
   * Per-outfit cut tweaks (BODY dressed-r2 review; all optional, every other robe drawn as before): `slitScale`
   * narrows a slit or open front; `slitFill` fills it with the robe's own cloth, its edges in shade (`lining`) or lays one lighter fold line down the
   * middle of what shows through (`fold`); `hemLift` raises the hem (art px) so the boot tops show; `panel` draws a
   * darker side-panel seam down the skirt; `sashRows` makes the sash deeper.
   */
  slitScale?: number;
  /** `seam`: a dark slit with one lighter line of the cloth down its middle. */
  slitFill?: 'lining' | 'fold' | 'seam';
  /** How far the hem flares past the hips (art px; default 5 ankle-length, 3 knee-length). */
  hemFlare?: number;
  /** No train spreading on the ground. */
  noTrain?: boolean;
  hemLift?: number;
  panel?: boolean;
  sashRows?: number;
}

export const DEFAULT_ROBE: RobeLook = {
  id: 'hanfu',
  colour: '#3b55a3',
  trim: '#e8c27e',
  inner: '#f4ecd6',
  sash: '#c63c4a',
};

/** Review tools only: draw robes without the per-outfit cut tweaks (RobeLook `slitScale` … `sashRows`), for a before. */
let ROBE_TWEAKS = true;
export function setRobeTweaks(v: boolean): void {
  ROBE_TWEAKS = v;
}

/** A robe look without its cut tweaks. */
function plainRobe(look: RobeLook): RobeLook {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { slitScale: _a, slitFill: _b, hemLift: _c, panel: _d, sashRows: _e, hemFlare: _f, noTrain: _g, ...plain } = look;
  return plain;
}

export function robeLayer(look: RobeLook): Layer {
  const design = ROBES.find((r) => r.id === look.id);
  const { c, s } = cloth(look.colour);
  const sash = cloth(look.sash);
  return {
    id: 'robe',
    label: 'ชุดคลุม',
    pieces(sk) {
      if (!design) return [];
      // The robe borrows a top's collar and sleeves: a wrap collar, its own sleeve cut.
      const asTop: TopDesign = {
        id: design.id,
        label: design.label,
        sex: design.sex,
        length: 'hips',
        sleeve: 'long',
        collar: design.collar === 'stand' ? 'stand' : 'cross',
      };
      const top: TopLook = {
        id: design.id,
        colour: look.colour,
        trim: look.trim,
        inner: look.inner,
        accent: look.accent,
        brocade: look.brocade ?? (look.hem ? 'lattice' : undefined),
      };
      const out: Piece[] = [
        {
          z: zOf(sk, 'chest', 0.05),
          draw: (g) => {
            const cut = ROBE_TWEAKS ? look : plainRobe(look);
            const skirtRows = robeSkirt(g, sk, design, cut, c, s);
            torso(g, sk, c, s, 1.5, 'waist');
            const rows = torsoRows(sk, 1.5, 'waist');
            // Patterns that belong on the hem only stay off the body.
            const onBody = design.pattern !== 'band' && design.pattern !== 'pleats';
            decorate(
              g,
              sk,
              onBody ? [...rows, ...skirtRows] : skirtRows,
              design.pattern,
              c,
              s,
              look.trim,
              look.inner,
            );
            const rich = !!(look.brocade || look.hem);
            if (look.brocade) weave(g, [...rows, ...skirtRows], look.brocade, c);
            if (look.hem) hemBand(g, skirtRows, look.hem, look.trim, look.accent ?? look.inner, s);
            if (rich) creases(g, sk, s, shade(c, 0.06));
            collar(g, sk, asTop, top);
            if (design.collar === 'fur') furCollar(g, sk);
            robeSash(g, sk, sash, design.highWaist, rich ? (look.accent ?? look.trim) : undefined, cut.sashRows);
          },
        },
      ];
      if (design.sleeve !== 'none') {
        const cut: TopDesign = { ...asTop, sleeve: design.sleeve };
        for (const side of ['B', 'A'] as Side[])
          out.push({ z: zOf(sk, `shoulder${side}`, 0.05), draw: (g) => sleeve(g, sk, side, cut, top, c, s) });
      }
      return out;
    },
  };
}

/**
 * The embroidered band round the bottom of a robe's skirt, inside its hem: `rows` are the skirt's
 * rows (the hem itself excluded), the band takes the lowest seven. Drawn in the trim and a second
 * thread, with a line of piping above.
 */
function hemBand(
  g: G,
  rows: readonly { y: number; L: number; R: number; gap?: readonly [number, number] }[],
  motif: HemMotif,
  trim: string,
  accent: string,
  s: string,
): void {
  const n = Math.min(7, rows.length - 2);
  if (n < 4) return;
  const band = rows.slice(rows.length - n);
  // Peaks of the flames, column by column.
  const FLAME = [1, 3, 5, 3, 2, 4, 6, 4, 2, 3];
  band.forEach(({ y, L, R, gap }, r) => {
    for (let x = L + 1; x < R; x++) {
      if (gap && x >= gap[0] && x <= gap[1]) continue;
      const u = x - L;
      const up = n - 1 - r;
      let col: string | undefined;
      if (r === 0 && motif !== 'flames') col = accent;
      else
        switch (motif) {
          case 'waves': {
            // Rocks rising out of diagonal bands of sea.
            const peak = Math.abs((u % 9) - 4);
            if (r <= 3 && r >= 1 && peak < r) col = r === 1 ? accent : trim;
            else if (r >= 3) {
              const k = (u - r * 2 + 120) % 6;
              col = k < 2 ? trim : k < 4 ? accent : undefined;
            }
            break;
          }
          case 'clouds': {
            const k = (u + (r > 3 ? 3 : 0)) % 7;
            const row = r > 3 ? r - 4 : r - 1;
            if ((row === 0 && (k === 1 || k === 2)) || (row === 1 && k >= 0 && k <= 3 && k !== 1)) col = trim;
            if (r === n - 1) col = accent;
            break;
          }
          case 'flames': {
            const h = FLAME[u % FLAME.length]!;
            if (up < h) col = up < h - 2 ? accent : trim;
            break;
          }
          case 'lotus': {
            // A row of lotus flowers, petals opening upward.
            const k = (u % 7) - 3;
            const w = [0, 1, 2, 3, 2][r - 1] ?? -1;
            if (r >= 1 && r <= 5 && Math.abs(k) <= w) col = k === 0 ? accent : trim;
            if (r === n - 1) col = accent;
            break;
          }
          case 'bamboo': {
            // Stalks with knots, a leaf off each.
            const k = u % 6;
            if (k === 1) col = r === 3 ? accent : trim;
            else if ((k === 2 && r === 2) || (k === 3 && r === 1)) col = accent;
            break;
          }
          case 'peony': {
            const k = u % 8;
            const d = Math.hypot(k - 3.5, r - 3);
            if (d < 1) col = accent;
            else if (d < 2.3) col = trim;
            else if ((k === 7 || k === 0) && r >= 3) col = s;
            break;
          }
        }
      if (col) px(g, x, y, 1, 1, col);
    }
  });
}

/** A thick ruff of pale fur round the neck and over the tops of the shoulders. */
function furCollar(g: G, sk: Skeleton): void {
  const j = sk.joints;
  const [shw] = torsoWidths(sk);
  const fur = '#d8ccb8';
  const furS = '#a89a84';
  const y = Math.round(j.chest.y) - 2;
  const hw = Math.round(shw * 0.8);
  px(g, Math.round(j.chest.x) - hw, y, hw * 2 + 1, 3, fur);
  for (let x = -hw; x <= hw; x += 2) px(g, Math.round(j.chest.x) + x, y + 3, 1, 1, fur);
  px(g, Math.round(j.chest.x) - hw, y + 2, hw * 2 + 1, 1, furS);
}

/**
 * The robe's skirt, from the sash to the hem: an A-line that widens enough to clear the feet in a
 * stride, centred between them, with folds fanning out from the waist. An open front leaves a
 * widening gap undrawn so the clothes underneath show; turned away, the back panel is whole.
 * Returns its rows, for decoration.
 */
function robeSkirt(
  g: G,
  sk: Skeleton,
  d: RobeDesign,
  look: RobeLook,
  c: string,
  s: string,
): { y: number; L: number; R: number; gap?: readonly [number, number] }[] {
  const j = sk.joints;
  const [, whw, hhw] = torsoWidths(sk);
  const feet = { x: (j.ankleA.x + j.ankleB.x) / 2, y: Math.max(j.ankleA.y, j.ankleB.y) };
  const hemY = (d.length === 'ankle' ? feet.y - 1 + (d.train && !look.noTrain ? 2 : 0) : (j.kneeA.y + j.kneeB.y) / 2 + 2) - (look.hemLift ?? 0);
  const stride = Math.abs(j.ankleA.x - j.ankleB.x);
  const hemHw = Math.max(hhw + (look.hemFlare ?? (d.length === 'ankle' ? 5 : 3)), stride / 2 + 3);
  const train = d.train && !look.noTrain;
  const y0 = Math.round(j.waist.y);
  const y1 = Math.round(hemY);
  const facing = sk.square ? 0 : 1;
  const rich = !!(look.brocade || look.hem);
  const hi = shade(c, 0.06);
  const rows: { y: number; L: number; R: number; gap?: readonly [number, number] }[] = [];
  for (let y = y0; y <= y1; y++) {
    const t = y1 > y0 ? (y - y0) / (y1 - y0) : 0;
    const cx = j.waist.x + (feet.x - j.waist.x) * t;
    const hw = whw + 1.5 + (hemHw - whw - 1.5) * Math.pow(t, 0.85);
    let L = Math.round(cx - hw);
    let R = Math.round(cx + hw);
    // A train spreads out on the ground behind (both ways head-on) over the last rows.
    if (train && t > 0.75) {
      const k = Math.round((t - 0.75) * 24);
      if (sk.square) {
        L -= Math.round(k / 2);
        R += Math.round(k / 2);
      } else L -= k;
    }
    const edge = y >= y1 - 1;
    // The opening: centred head-on, toward the facing side when turned; none from behind.
    const open = (d.front === 'open' && t > 0.08) || (d.front === 'slit' && t > 0.45);
    const opening = open && !sk.back;
    const gap = opening
      ? Math.round(
          (d.front === 'slit' ? (t - 0.45) * (sk.view === 'side' ? 2 : 4) : t * (sk.view === 'side' ? 1.5 : 3.5)) *
            (look.slitScale ?? 1),
        )
      : -1;
    const mid = Math.round(cx + facing * (sk.view === 'side' ? hw - 2 : 2));
    for (let x = L; x <= R; x++) {
      if (opening && Math.abs(x - mid) <= gap) {
        // The lining across the opening, or one fold line down the middle of what shows through it.
        if (!edge && look.slitFill === 'lining') px(g, x, y, 1, 1, Math.abs(x - mid) === gap ? s : c);
        else if (!edge && look.slitFill === 'fold' && x === mid && gap >= 1) px(g, x, y, 1, 1, s);
        else if (!edge && look.slitFill === 'seam') px(g, x, y, 1, 1, x === mid && gap >= 1 ? c : s);
        continue;
      }
      let col = edge ? look.trim : c;
      if (!edge && (x === L || x === L + 1)) col = s;
      // Folds fanning out from the sash, a shade line every few columns lower down.
      else if (!edge && t > 0.3 && (x - L) % 4 === 2 && (y + x) % 3 !== 0) col = s;
      // Fine robes: the lit ridge of each fold beside its shadow, a shadow under the sash, and the
      // hem beaded with the second thread.
      else if (rich && !edge && t > 0.35 && (x - L) % 4 === 0 && (y + x) % 3 !== 1) col = hi;
      else if (rich && y - y0 < 1) col = s;
      if (rich && y === y1 && (x - L) % 3 === 1) col = look.accent ?? look.inner;
      if (opening && !edge && Math.abs(x - mid) === gap + 1) col = look.trim;
      // A side-panel seam a few columns in from each edge (front and back), darker than the cloth.
      if (look.panel && !edge && t > 0.08 && (x === L + 3 || (sk.square && x === R - 3))) col = s;
      px(g, x, y, 1, 1, col);
    }
    if (!edge)
      rows.push({ y, L: L + 1, R: R - 1, gap: opening ? [mid - gap - 1, mid + gap + 1] : undefined });
  }
  paintCurves(g, sk, 2.5, 'seat', c, s);
  return rows;
}

/** A cloth sash tied over the waist (or high under the chest), the knot's tails hanging on the facing side. */
function robeSash(g: G, sk: Skeleton, sash: { c: string; s: string }, high = false, accent?: string, rows = 2): void {
  const j = sk.joints;
  const [shw, whw] = torsoWidths(sk);
  const y = high ? Math.round(j.chest.y + (j.waist.y - j.chest.y) * 0.45) : Math.round(j.waist.y) - 2;
  const hw = high ? (shw + whw) / 2 + 1.5 : whw + 2;
  const cx = high ? j.chest.x + (j.waist.x - j.chest.x) * 0.45 : j.waist.x;
  const L = Math.round(cx - hw);
  const W = Math.round(hw * 2) + 1;
  // A deeper sash grows upward from the same lower edge.
  const top = y - (rows - 2);
  px(g, L, top, W, rows, sash.c);
  px(g, L, y + 1, W, 1, sash.s);
  if (accent) {
    // A woven sash: a lit upper edge and a running pattern of the second thread.
    px(g, L, y, W, 1, shade(sash.c, 0.08));
    for (let x = L + 1; x < L + W - 1; x += 3) px(g, x, y, 1, 1, accent);
  }
  if (sk.back) return;
  const kx = Math.round(cx + (sk.square ? -1 : 2));
  const tail = high ? 7 : 4;
  if (accent) {
    // A bow: two loops either side of the knot, the ends hanging with fringed tips.
    px(g, kx - 2, y - 1, 2, 2, sash.c);
    px(g, kx + 1, y - 1, 2, 2, sash.c);
    px(g, kx - 2, y, 1, 1, sash.s);
    px(g, kx + 2, y, 1, 1, sash.s);
    px(g, kx, y - 1, 1, 3, sash.s);
    px(g, kx, y, 1, 1, accent);
    px(g, kx - 1, y + 2, 1, tail + 1, sash.c);
    px(g, kx + 1, y + 2, 1, tail, sash.s);
    px(g, kx - 1, y + 3 + tail, 1, 1, accent);
    px(g, kx + 1, y + 2 + tail, 1, 1, accent);
    return;
  }
  px(g, kx, y + 2, 1, tail, sash.c);
  px(g, kx + 2, y + 2, 1, tail - 1, sash.s);
}

/** `a` modulo `n`, never negative. */
const mod = (a: number, n: number) => ((a % n) + n) % n;

/**
 * Weaves a brocade into cloth already drawn in `rows`: the motif is laid out from the centre of each
 * row and the top of the garment, so it stays put on the body as it moves. Light cloth only takes
 * the darker thread (a lighter one would vanish into it).
 */
function weave(
  g: G,
  rows: readonly { y: number; L: number; R: number; gap?: readonly [number, number] }[],
  kind: Brocade,
  c: string,
): void {
  if (!rows.length) return;
  const lo = shade(c, -0.055);
  const light = toHsl(c)[2] > 0.8;
  const hi = light ? undefined : shade(c, 0.07);
  const top = Math.min(...rows.map((r) => r.y));
  for (const { y, L, R, gap } of rows) {
    const mid = Math.round((L + R) / 2);
    const v = y - top;
    for (let x = L + 2; x < R - 1; x++) {
      if (gap && x >= gap[0] - 1 && x <= gap[1] + 1) continue;
      const u = x - mid;
      let col: string | undefined;
      switch (kind) {
        case 'lattice':
          if (mod(u + v, 6) === 0 || mod(u - v, 6) === 0) col = lo;
          else if (mod(u, 6) === 3 && mod(v, 6) === 0) col = hi;
          break;
        case 'clouds': {
          const cu = mod(u + (Math.floor(v / 5) % 2) * 4, 8);
          const cv = mod(v, 5);
          if ((cv === 1 && (cu === 2 || cu === 3)) || (cv === 2 && cu >= 1 && cu <= 4 && cu !== 2)) col = lo;
          else if (cv === 2 && cu === 2) col = hi;
          break;
        }
        case 'scales': {
          // Fish scales: rounded arcs six wide, each row of them offset by half a scale.
          const cu = mod(u + (Math.floor(v / 4) % 2) * 3, 6);
          const cv = mod(v, 4);
          if ((cv === 3 && cu >= 2 && cu <= 4) || (cv === 2 && (cu === 1 || cu === 5))) col = lo;
          else if (cv === 0 && cu === 3) col = hi;
          break;
        }
        case 'blossoms': {
          const cu = mod(u + (Math.floor(v / 5) % 2) * 3, 6);
          const cv = mod(v, 5);
          if (cu === 3 && cv === 2) col = hi ?? lo;
          else if ((Math.abs(cu - 3) === 1 && cv === 2) || (cu === 3 && Math.abs(cv - 2) === 1)) col = lo;
          break;
        }
        case 'fret': {
          // A key fret (回紋): a hooked spiral in each 5 by 5 cell.
          const cell = ['xxxx.', '...x.', '.x.x.', '.xxx.', '.....'];
          if (cell[mod(v, 5)]![mod(u, 5)] === 'x') col = lo;
          break;
        }
      }
      if (col) px(g, x, y, 1, 1, col);
    }
  }
}

/**
 * Where cloth pulls: creases fanning up from the sash toward the arms in front, the centre seam
 * down the back, each a shadow with a lit edge beside it.
 */
function creases(g: G, sk: Skeleton, s: string, hi: string): void {
  const j = sk.joints;
  const [shw, whw] = torsoWidths(sk);
  const wy = Math.round(j.waist.y) - 3;
  if (sk.back) {
    for (let y = Math.round(j.chest.y) + 1; y < wy; y++) px(g, Math.round(j.chest.x), y, 1, 1, s);
    // Shoulder-blade folds.
    for (const d of [-1, 1]) {
      px(g, Math.round(j.chest.x + d * (shw - 2)), Math.round(j.chest.y) + 3, 1, 2, s);
      px(g, Math.round(j.chest.x + d * (shw - 3)), Math.round(j.chest.y) + 4, 1, 1, hi);
    }
    return;
  }
  const cx = Math.round(j.waist.x + (sk.square ? 0 : 1));
  for (const d of sk.view === 'side' ? [1] : [-1, 1]) {
    const x0 = cx + d * Math.max(1, whw - 3);
    for (let i = 0; i < 3; i++) {
      px(g, x0 + d * i, wy - i, 1, 1, s);
      px(g, x0 + d * i - d, wy - i, 1, 1, hi);
    }
  }
}
